<template>
  <div class="adm-grid">
    <div class="adm-field adm-span-2">
      <label class="adm-label" :for="`${uid}-name`">Clinic name <span class="adm-req">*</span></label>
      <input :id="`${uid}-name`" class="adm-input" :class="{ 'is-invalid': errors.clinicname }" maxlength="100"
             :value="modelValue.clinicname" placeholder="e.g. City Care Hospital"
             @input="set('clinicname', $event.target.value)" />
      <div v-if="errors.clinicname" class="adm-field__error">{{ errors.clinicname }}</div>
    </div>

    <div class="adm-field">
      <label class="adm-label" :for="`${uid}-city`">City <span class="adm-req">*</span></label>
      <select :id="`${uid}-city`" class="adm-select" :class="{ 'is-invalid': errors.city_id }"
              :value="modelValue.city_id || ''" @change="setCity($event.target.value)">
        <option value="" disabled>{{ cities.length ? "Select city" : "Loading cities…" }}</option>
        <option v-for="city in cities" :key="city.city_id" :value="city.city_id">{{ city.name }}</option>
      </select>
      <div v-if="errors.city_id" class="adm-field__error">{{ errors.city_id }}</div>
    </div>

    <div class="adm-field">
      <label class="adm-label" :for="`${uid}-id`">Clinic ID <span class="adm-req">*</span></label>
      <input :id="`${uid}-id`" class="adm-input adm-mono" :class="{ 'is-invalid': errors.clinicid }" maxlength="10"
             :value="modelValue.clinicid" placeholder="CITYCAREMLR"
             @input="setId($event.target.value)" />
      <div v-if="errors.clinicid" class="adm-field__error">{{ errors.clinicid }}</div>
      <div v-else class="adm-field__help">3–10 letters or digits. Suggested from the name and city.</div>
    </div>

    <div class="adm-field">
      <label class="adm-label" :for="`${uid}-phone`">Contact number <span class="adm-req">*</span></label>
      <input :id="`${uid}-phone`" class="adm-input" :class="{ 'is-invalid': errors.contactnumbers }" maxlength="50"
             :value="modelValue.contactnumbers" placeholder="+91 98765 43210"
             @input="set('contactnumbers', $event.target.value)" />
      <div v-if="errors.contactnumbers" class="adm-field__error">{{ errors.contactnumbers }}</div>
    </div>

    <div class="adm-field">
      <label class="adm-label" :for="`${uid}-email`">Email</label>
      <input :id="`${uid}-email`" type="email" class="adm-input" :class="{ 'is-invalid': errors.email }" maxlength="100"
             :value="modelValue.email" placeholder="frontdesk@clinic.in"
             @input="set('email', $event.target.value)" />
      <div v-if="errors.email" class="adm-field__error">{{ errors.email }}</div>
    </div>

    <div class="adm-field adm-span-all">
      <label class="adm-label" :for="`${uid}-address`">Address</label>
      <textarea :id="`${uid}-address`" class="adm-textarea" rows="2" maxlength="500"
                :value="modelValue.clinicaddress" placeholder="Street, area, landmark"
                @input="set('clinicaddress', $event.target.value)"></textarea>
    </div>
  </div>
</template>

<script>
import { EMAIL_RE } from "../admin-utils";

let nextId = 0;

export function emptyClinic() {
  return { clinicid: "", clinicname: "", city_id: null, locationname: "", clinicaddress: "", contactnumbers: "", email: "" };
}

/** "City Care Hospital" in Mangalore -> CITYCARMAN (first two words + city). */
export function suggestClinicId(form, cities) {
  const words = form.clinicname.toUpperCase().replace(/[^A-Z0-9 ]/g, " ").split(/\s+/).filter(Boolean);
  const city = cities.find(c => c.city_id === form.city_id);
  const base = words.slice(0, 2).map((w, i) => w.slice(0, i ? 3 : 4)).join("");
  const suffix = city ? city.name.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3) : "";
  return (base + suffix).slice(0, 10);
}

/** Field -> message for an invalid clinic form ({} when valid). */
export function validateClinic(form) {
  const errors = {};
  if (!form.clinicname.trim()) errors.clinicname = "Enter the clinic name.";
  if (!form.city_id) errors.city_id = "Select a city.";
  if (!/^[A-Z0-9]{3,10}$/.test(form.clinicid)) errors.clinicid = "Use 3–10 capital letters or digits.";
  if (!form.contactnumbers.trim()) errors.contactnumbers = "Enter a contact number.";
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) errors.email = "Enter a valid email.";
  return errors;
}

/** Request body for POST /doctors/clinichospital or the approve endpoint. */
export function clinicPayload(form) {
  return {
    clinicid: form.clinicid,
    clinicname: form.clinicname.trim(),
    city_id: form.city_id,
    locationname: form.locationname,
    clinicaddress: form.clinicaddress.trim() || null,
    contactnumbers: form.contactnumbers.trim(),
    email: form.email.trim() || null,
  };
}

export default {
  name: "ClinicFormFields",
  props: {
    modelValue: { type: Object, required: true },
    cities: { type: Array, default: () => [] },
    errors: { type: Object, default: () => ({}) },
  },
  emits: ["update:modelValue"],
  data() {
    return { uid: `adm-clinic-${++nextId}`, idEdited: false };
  },
  methods: {
    set(field, value) {
      const next = { ...this.modelValue, [field]: value };
      if (!this.idEdited && (field === "clinicname" || field === "city_id")) next.clinicid = this.suggestId(next);
      this.$emit("update:modelValue", next);
    },
    setCity(value) {
      const city = this.cities.find(c => String(c.city_id) === String(value));
      const next = { ...this.modelValue, city_id: city ? city.city_id : null, locationname: city ? city.name.slice(0, 25) : "" };
      if (!this.idEdited) next.clinicid = this.suggestId(next);
      this.$emit("update:modelValue", next);
    },
    setId(value) {
      this.idEdited = value !== "";
      this.$emit("update:modelValue", { ...this.modelValue, clinicid: value.toUpperCase().replace(/[^A-Z0-9]/g, "") });
    },
    suggestId(form) {
      return suggestClinicId(form, this.cities);
    },
  },
};
</script>
